import React, { useEffect, useState } from "react";
import moment from "moment";

import PanelTemplate from "../../../templates/Panel";
import Box from "../../../components/Box";
import Table from "../../../components/Table";
import Button from "../../../components/Button";
import InputDate from "../../../components/InputDate";
import InputSelect from "../../../components/InputSelect";
import Icon from "../../../components/Icon";
import CashFlowMatrix from "./matrix";

import api from "../../../services/api";
import { getToken } from "../../../services/auth";
import { getSubdomain } from "../../../helpers/domain";
import { renderError } from "../../../helpers/errors";
import { createOptions } from "../../../helpers/selects";
import { convertIntToMoney } from "../../../helpers/conversions";
import {
    getBillingCompanyLabel,
    getStoredBillingCompany,
    storeBillingCompany,
    useBillingCompanies,
} from "../billingCompany";

const VIEW_MODES = [
    { key: 'monthly', label: 'Mensal' },
    { key: 'daily', label: 'Diário' },
];

/**
 * Atalhos de período, por forma de visualização: no mensal só faz sentido
 * oferecer recortes de vários meses, no diário recortes curtos — 365 linhas de
 * um dia cada não se leem.
 */
const PERIOD_PRESETS = {
    monthly: [
        {
            key: 'this-year',
            label: 'Este ano',
            range: () => [moment().startOf('year'), moment().endOf('year')],
        },
        {
            key: 'last-6-months',
            label: 'Últimos 6 meses',
            range: () => [moment().subtract(5, 'months').startOf('month'), moment().endOf('month')],
        },
        {
            key: 'next-6-months',
            label: 'Próximos 6 meses',
            range: () => [moment().startOf('month'), moment().add(5, 'months').endOf('month')],
        },
        {
            key: 'last-year',
            label: 'Ano passado',
            range: () => [moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year')],
        },
    ],
    daily: [
        {
            key: 'this-month',
            label: 'Este mês',
            range: () => [moment().startOf('month'), moment().endOf('month')],
        },
        {
            key: 'next-30-days',
            label: 'Próximos 30 dias',
            range: () => [moment(), moment().add(30, 'days')],
        },
        {
            key: 'next-3-months',
            label: 'Próximos 3 meses',
            range: () => [moment().startOf('month'), moment().add(2, 'months').endOf('month')],
        },
        {
            key: 'last-month',
            label: 'Mês passado',
            range: () => [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
        },
    ],
};

const DEFAULT_PRESET = mode => PERIOD_PRESETS[mode][0];

/**
 * As colunas se repetem (entradas/saídas/saldo final) nos dois blocos: quem
 * distingue é o título do grupo no topo, então "previsto"/"realizado" aparecem
 * uma única vez cada.
 */
const HEADER_GROUPS = [
    { label: '', colSpan: 1 },
    {
        label: (
            <span className="flow-group">
                <Icon name="calendar" size={20} />
                Previsto
            </span>
        ),
        colSpan: 3,
        className: 'flow-group-header flow-group-header--divider',
    },
    {
        label: (
            <span className="flow-group">
                <Icon name="check" size={20} />
                Realizado
            </span>
        ),
        colSpan: 3,
        className: 'flow-group-header flow-group-header--divider',
    },
];

const HEADER = ['Período', 'Entradas', 'Saídas', 'Saldo final', 'Entradas', 'Saídas', 'Saldo final'];

const signedValue = value => (
    <span className={value > 0 ? 'value-positive' : (value < 0 ? 'value-negative' : '')}>
        {convertIntToMoney(value, true)}
    </span>
);

export const FinanceCashFlow = () => {
    const [loading, setLoading] = useState(true);

    const [mode, setMode] = useState('monthly');
    const [preset, setPreset] = useState(DEFAULT_PRESET('monthly').key);
    const [startDate, setStartDate] = useState(DEFAULT_PRESET('monthly').range()[0].format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(DEFAULT_PRESET('monthly').range()[1].format('YYYY-MM-DD'));
    const [bankAccountId, setBankAccountId] = useState(null);
    const [billingCompanyId, setBillingCompanyId] = useState(getStoredBillingCompany());

    const { billingCompanies, hasMultiple: hasMultipleBillingCompanies } = useBillingCompanies();

    const [bankAccounts, setBankAccounts] = useState([]);
    const [balances, setBalances] = useState([]);
    const [flow, setFlow] = useState([]);
    const [matrix, setMatrix] = useState(null);

    const periodIsValid = !!startDate && !!endDate && !moment(endDate).isBefore(startDate);

    useEffect(() => {
        getBankAccounts();
    }, []);

    useEffect(() => {
        getBalances();
    }, [billingCompanyId]);

    // Filtro aplica na hora: não há botão "Aplicar". Período inválido não vira
    // requisição — a mensagem no filtro já diz o que corrigir.
    useEffect(() => {
        if (!periodIsValid) {
            return;
        }

        getFlow();
    }, [mode, startDate, endDate, bankAccountId, billingCompanyId]);

    // Mesma autodefesa da listagem: empresa guardada que não existe mais
    // deixaria o fluxo permanentemente vazio, sem nada explicando o porquê.
    useEffect(() => {
        if (billingCompanies.length === 0 || !billingCompanyId) return;
        if (billingCompanies.some(company => company.id === billingCompanyId)) return;

        storeBillingCompany('');
        setBillingCompanyId('');
    }, [billingCompanies]);

    const getBalances = () => {
        const query = billingCompanyId ? `?billing_company_id=${billingCompanyId}` : '';

        api.get(`/finance-report/balances${query}`).then(res => {
            setBalances(res.data.resources);
        }).catch(renderError);
    };

    const getBankAccounts = () => {
        api.get(`/finance-bank-account`).then(res => {
            setBankAccounts(res.data.resources.filter(account => account.active));
        }).catch(() => {});
    };

    const getFlow = () => {
        setLoading(true);

        const params = new URLSearchParams({
            start_date: startDate,
            end_date: endDate,
        });

        if (mode === 'daily') {
            params.append('group_by', 'day');
        }

        if (bankAccountId) {
            params.append('finance_bank_account_id', bankAccountId);
        }

        if (billingCompanyId) {
            params.append('billing_company_id', billingCompanyId);
        }

        const endpoint = mode === 'monthly' ? 'monthly-cash-flow' : 'cash-flow';

        api.get(`/finance-report/${endpoint}?${params.toString()}`).then(res => {
            if (mode === 'monthly') {
                setMatrix(res.data.resources);
            } else {
                setFlow(res.data.resources);
            }
        }).catch(renderError).then(() => setLoading(false));
    };

    const handleBillingCompany = id => {
        storeBillingCompany(id);
        setBillingCompanyId(id);
    };

    // Trocar de visualização recorta o período no atalho padrão dela: o
    // recorte de um mês do diário viraria uma coluna só no mensal.
    const handleMode = key => {
        setMode(key);
        handlePreset(DEFAULT_PRESET(key));
    };

    const handlePreset = presetToApply => {
        const [start, end] = presetToApply.range();

        setPreset(presetToApply.key);
        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end.format('YYYY-MM-DD'));
    };

    // Mexer nas datas na mão desliga o atalho ativo.
    const handleStartDate = date => {
        setPreset(null);
        setStartDate(date);
    };

    const handleEndDate = date => {
        setPreset(null);
        setEndDate(date);
    };

    const rows = flow.map(period => ({
        data: [
            moment(period.period).format('DD/MM/YYYY'),
            convertIntToMoney(period.expected_income, true),
            convertIntToMoney(period.expected_expense, true),
            signedValue(period.expected_net),
            convertIntToMoney(period.realized_income, true),
            convertIntToMoney(period.realized_expense, true),
            signedValue(period.realized_net),
        ],
    }));

    const exportExcel = () => {
        const params = new URLSearchParams({
            start_date: startDate,
            end_date: endDate,
            view: mode,
            _token: getToken(),
            _subdomain: getSubdomain(),
        });

        if (bankAccountId) params.append('finance_bank_account_id', bankAccountId);
        if (billingCompanyId) params.append('billing_company_id', billingCompanyId);

        window.open(`${process.env.REACT_APP_URL}/finance-report/cash-flow/export?${params.toString()}`);
    };

    return (
        <PanelTemplate id="finance-cash-flow">
            <div className="finance-cash-flow">
                <Box>
                    <div className="finance-cash-flow-toolbar">
                        <div className="finance-view-modes">
                            {VIEW_MODES.map(item => (
                                <button
                                    key={item.key}
                                    type="button"
                                    className={`view-mode${mode === item.key ? ' active' : ''}`}
                                    onClick={() => handleMode(item.key)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        {/* Download por URL com token na query, como as demais
                            exportações do sistema: o endpoint é GET e o
                            navegador não manda o header de autenticação. */}
                        <Button
                            type={`secondary`}
                            size={`small`}
                            text={`Exportar Excel`}
                            disabled={!periodIsValid}
                            action={exportExcel}
                        />
                    </div>

                    <div className="finance-period-presets">
                        {PERIOD_PRESETS[mode].map(item => (
                            <button
                                key={item.key}
                                type="button"
                                className={`period-preset${preset === item.key ? ' active' : ''}`}
                                onClick={() => handlePreset(item)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <div className="finance-filters">
                        {hasMultipleBillingCompanies &&
                            <div className="filter-field">
                                <InputSelect
                                    label={`Empresa`}
                                    options={[
                                        { label: 'Todas as empresas', value: '' },
                                        ...billingCompanies.map(company => ({ label: getBillingCompanyLabel(company), value: company.id })),
                                    ]}
                                    value={billingCompanyId}
                                    change={handleBillingCompany}
                                />
                            </div>
                        }
                        <div className="filter-field">
                            <InputDate label={`De`} format={`YYYY-MM-DD`} value={startDate} change={handleStartDate} />
                        </div>
                        <div className="filter-field">
                            <InputDate label={`Até`} format={`YYYY-MM-DD`} value={endDate} change={handleEndDate} />
                        </div>
                        <div className="filter-field">
                            <InputSelect
                                label={`Conta`}
                                options={createOptions(bankAccounts, 'name', 'id', true, null, 'Todas as contas')}
                                value={bankAccountId}
                                change={setBankAccountId}
                            />
                        </div>
                    </div>

                    {!periodIsValid &&
                        <span className="finance-period-error">
                            Informe as duas datas — a data final precisa ser igual ou posterior à inicial.
                        </span>
                    }
                </Box>

                {balances.length === 0 && billingCompanyId && !loading &&
                    <span className="finance-balances-empty">
                        Nenhuma conta bancária atribuída a esta empresa. Defina a empresa de cada conta nas configurações do financeiro.
                    </span>
                }

                {balances.length > 0 &&
                    <>
                        <div className="finance-balances">
                            {balances.map(account => (
                                <div key={account.id} className="finance-balance-card">
                                    <div className="account-name">
                                        {account.name}{!account.active ? ' (inativa)' : ''}
                                        {hasMultipleBillingCompanies && account.without_billing_company &&
                                            <span className="account-without-company" title={`Defina de qual empresa é esta conta nas configurações do financeiro`}>sem empresa</span>
                                        }
                                    </div>
                                    <div className={`account-balance ${account.balance < 0 ? 'negative' : ''}`}>
                                        {convertIntToMoney(account.balance, true)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                }

                <Box className={`no-padding`}>
                    {mode === 'monthly'
                        ? <CashFlowMatrix data={matrix} loading={loading} showTransfers={!!bankAccountId} />
                        : <Table header={HEADER} headerGroups={HEADER_GROUPS} rows={rows} loading={loading} />
                    }
                </Box>
            </div>
        </PanelTemplate>
    );
};

export default FinanceCashFlow;
